'use client';

import { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import ReviewCard from './ReviewCard';


import CaseStudyCard from './CaseStudyCard';
import TrustBadges from './TrustBadges';

interface Review {
  id: number;
  name: string;
  service: string;
  rating: number;
  date: string;
  review: string;
  image: string;
  alt: string;
  verified: boolean;
}

interface BeforeAfter {
  id: number;
  title: string;
  service: string;
  beforeImage: string;
  beforeAlt: string;
  afterImage: string;
  afterAlt: string;
  description: string;
}

interface VideoTestimonial {
  id: number;
  name: string;
  title: string;
  thumbnail: string;
  thumbnailAlt: string;
  duration: string;
  service: string;
}

interface CaseStudy {
  id: number;
  title: string;
  service: string;
  duration: string;
  challenge: string;
  solution: string;
  result: string;
  image: string;
  alt: string;
}

export default function ReviewsInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTab, setActiveTab] = useState('reviews');
  const [selectedService, setSelectedService] = useState('all');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const reviews: Review[] = [
  {
    id: 1,
    name: "Sarah M",
    service: "Gutter Cleaning",
    rating: 5,
    date: "December 2025",
    review: "Crystal Clean has been maintaining our home for over three years now. They show up exactly when scheduled, work efficiently, and treat our property with genuine care. The peace of mind knowing our gutters are properly maintained is worth every penny.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14da91c34-1763294780479.png",
    alt: "Professional woman with shoulder-length brown hair wearing navy blazer smiling at camera",
    verified: true
  },
  {
    id: 2,
    name: "Michael C",
    service: "Window Cleaning",
    rating: 5,
    date: "November 2025",
    review: "After trying several window cleaning services, we finally found Crystal Clean. Their attention to detail is remarkable—they clean the tracks, screens, and sills, not just the glass. Our home looks pristine, and they're always professional and courteous.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_109c0a4b4-1763300818329.png",
    alt: "Asian man with short black hair and beard wearing white shirt in bright office setting",
    verified: true
  },
  {
    id: 3,
    name: "Jennifer Rodriguez",
    service: "Roof Cleaning",
    rating: 5,
    date: "October 2025",
    review: "Our roof had significant moss buildup that we were worried about. Crystal Clean explained the entire process, worked carefully to protect our landscaping, and the results exceeded our expectations. They truly care about preserving your home's value.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13b401b81-1763293456584.png",
    alt: "Hispanic woman with long dark hair wearing professional attire smiling warmly",
    verified: true
  },
  {
    id: 4,
    name: "David Thompson",
    service: "Exterior Washing",
    rating: 5,
    date: "September 2025",
    review: "We've been customers for five years and wouldn't trust anyone else with our home's exterior. Crystal Clean's consistency and reliability are unmatched. They remember our preferences, communicate proactively, and deliver exceptional results every single time.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1adc89ed4-1763294445054.png",
    alt: "Caucasian man with gray hair and glasses wearing blue button-down shirt outdoors",
    verified: true
  },
  {
    id: 5,
    name: "Lisa Anderson",
    service: "Gutter Cleaning",
    rating: 5,
    date: "August 2025",
    review: "As a busy professional, I need service providers I can trust completely. Crystal Clean has never let me down. They handle everything professionally, send reminders for seasonal maintenance, and their work is always thorough and careful.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cdcb4aa6-1767165720112.png",
    alt: "Blonde woman with wavy hair wearing casual sweater smiling in natural lighting",
    verified: true
  },
  {
    id: 6,
    name: "Robert Williams",
    service: "Window Cleaning",
    rating: 5,
    date: "July 2025",
    review: "After retirement, maintaining our home became challenging. Crystal Clean has been a godsend—they're patient, explain everything clearly, and treat our property like it's their own. The quality of their work reflects their genuine pride in what they do.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18900111e-1766772615459.png",
    alt: "Senior man with white hair wearing casual polo shirt smiling confidently",
    verified: true
  }];


  const beforeAfterGallery: BeforeAfter[] = [
  {
    id: 1,
    title: "Moss-Covered Roof Restoration",
    service: "Roof Cleaning",
    beforeImage: "https://images.unsplash.com/photo-1694565157570-746384765dee",
    beforeAlt: "Residential roof with heavy green moss coverage and dark staining on gray shingles",
    afterImage: "https://images.unsplash.com/photo-1707897283683-00a5b942a167",
    afterAlt: "Same residential roof completely clean showing pristine gray shingles without any moss or staining",
    description: "Complete moss removal and soft washing restored this roof to like-new condition, extending its lifespan by years."
  },
  {
    id: 2,
    title: "Clogged Gutter System Revival",
    service: "Gutter Cleaning",
    beforeImage: "https://images.unsplash.com/photo-1665599218809-670be8fc3b3d",
    beforeAlt: "White house gutters overflowing with brown leaves and debris causing water overflow",
    afterImage: "https://img.rocket.new/generatedImages/rocket_gen_img_12cdb0de8-1769645913092.png",
    afterAlt: "Same white house gutters completely clean and clear with proper water flow",
    description: "Removed years of debris buildup, preventing water damage and ensuring proper drainage throughout the property."
  },
  {
    id: 3,
    title: "Exterior Siding Transformation",
    service: "Exterior Washing",
    beforeImage: "https://images.unsplash.com/photo-1690475781290-05f0f20bed96",
    beforeAlt: "Two-story house with dirty gray siding showing dark mold stains and weathering",
    afterImage: "https://img.rocket.new/generatedImages/rocket_gen_img_11274bde6-1765292563578.png",
    afterAlt: "Same two-story house with pristine clean gray siding looking freshly painted",
    description: "Professional soft washing removed years of grime, mold, and mildew, revealing the home's true beauty."
  },
  {
    id: 4,
    title: "Crystal Clear Window Revival",
    service: "Window Cleaning",
    beforeImage: "https://img.rocket.new/generatedImages/rocket_gen_img_12ea953f7-1765086838033.png",
    beforeAlt: "Large residential windows with visible water spots, dirt streaks, and cloudy appearance",
    afterImage: "https://images.unsplash.com/photo-1641421247829-fd1a269f4bec",
    afterAlt: "Same large residential windows perfectly clean and clear reflecting blue sky",
    description: "Meticulous cleaning of all windows, tracks, and screens brought back stunning clarity and natural light."
  }];


  const videoTestimonials: VideoTestimonial[] = [
  {
    id: 1,
    name: "Patricia Johnson",
    title: "15 Years of Trust and Quality",
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1104c87ff-1765607754886.png",
    thumbnailAlt: "Elderly woman with gray hair wearing glasses and cardigan sitting in bright living room",
    duration: "2:45",
    service: "All Services"
  },
  {
    id: 2,
    name: "Mark Stevens",
    title: "From Skeptic to Loyal Customer",
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1dc64ab65-1763293842844.png",
    thumbnailAlt: "Middle-aged man with short brown hair wearing casual shirt standing in front of modern house",
    duration: "3:12",
    service: "Roof Cleaning"
  },
  {
    id: 3,
    name: "Emily Parker",
    title: "Peace of Mind for Busy Professionals",
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1bb208ce5-1763292286266.png",
    thumbnailAlt: "Young professional woman with dark hair in business attire smiling in office environment",
    duration: "2:28",
    service: "Gutter Cleaning"
  }];


  const caseStudies: CaseStudy[] = [
  {
    id: 1,
    title: "Historic Home Preservation",
    service: "Exterior Washing",
    duration: "3 Months",
    challenge: "100-year-old Victorian home with delicate original siding showing severe mold and mildew damage. Previous cleaning attempts had caused paint damage.",
    solution: "Developed custom low-pressure soft washing protocol with pH-balanced solutions. Conducted test areas and worked in small sections to ensure no damage to historic materials.",
    result: "Successfully removed all biological growth while preserving original paint and siding. Homeowners now on quarterly maintenance schedule to prevent future buildup.",
    image: "https://images.unsplash.com/photo-1629309344744-9312b52ee6f7",
    alt: "Beautiful Victorian-style house with ornate white trim and multiple gables showing pristine exterior"
  },
  {
    id: 2,
    title: "Commercial Property Maintenance",
    service: "Window Cleaning",
    duration: "Ongoing",
    challenge: "Multi-story office building with 200+ windows requiring monthly cleaning without disrupting business operations.",
    solution: "Implemented early morning cleaning schedule with specialized equipment for high-reach windows. Created detailed tracking system for consistent results.",
    result: "Five-year partnership with 100% on-time service record. Building maintains professional appearance year-round with zero tenant complaints.",
    image: "https://images.unsplash.com/photo-1721240608299-cdf5ee26c235",
    alt: "Modern multi-story office building with large glass windows reflecting clear blue sky"
  }];


  const services = ['all', 'Gutter Cleaning', 'Window Cleaning', 'Roof Cleaning', 'Exterior Washing'];

  const filteredReviews = selectedService === 'all' ?
  reviews :
  reviews.filter((review) => review.service === selectedService);

  const filteredBeforeAfter = selectedService === 'all' ?
  beforeAfterGallery :
  beforeAfterGallery.filter((item) => item.service === selectedService);

  // Carousel navigation functions
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % filteredReviews.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + filteredReviews.length) % filteredReviews.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  // Touch handlers for swipe functionality
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 75) {
      // Swiped left
      nextSlide();
    }

    if (touchStart - touchEnd < -75) {
      // Swiped right
      prevSlide();
    }
  };

  // Reset to first slide when filter changes
  useEffect(() => {
    setCurrentSlide(0);
  }, [selectedService]);

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="h-20"></div>
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <h1 className="font-serif text-4xl md:text-5xl font-semibold text-foreground mb-4">
              Customer Reviews
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Loading reviews...
            </p>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <div className="h-20"></div>

      <section className="bg-gradient-to-br from-primary to-secondary text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-semibold mb-6">
            What Our Customers Say
          </h1>
          <p className="text-lg md:text-xl opacity-90 max-w-3xl mx-auto mb-8">
            Real experiences from homeowners who trust Crystal Clean for their exterior maintenance needs
          </p>
        </div>
      </section>

      <section className="py-16 bg-surface">
        <div className="container mx-auto px-4">
          <TrustBadges />
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 space-y-4 md:space-y-0">
            <div className="flex items-center space-x-4 overflow-x-auto pb-2 w-full md:w-auto">
              <button
                onClick={() => setActiveTab('reviews')}
                className={`px-6 py-2.5 rounded-full font-medium transition-smooth whitespace-nowrap btn-bubbly ${
                activeTab === 'reviews' ? 'bg-primary text-white shadow-brand' : 'bg-surface text-muted-foreground hover:bg-accent'}`
                }>

                Reviews
              </button>
            </div>

            <div className="flex items-center space-x-2 w-full md:w-auto">
              <select
                value={selectedService}
                onChange={(e) => setSelectedService(e.target.value)}
                className="px-4 py-2 bg-surface border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary w-full md:w-auto">

                {services.map((service) =>
                <option key={service} value={service}>
                    {service === 'all' ? 'All Services' : service}
                  </option>
                )}
              </select>
            </div>
          </div>

          {activeTab === 'reviews' &&
          <div className="relative">
              {/* Carousel Container */}
              <div
              ref={carouselRef}
              className="overflow-hidden"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}>

                <div
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}>

                  {filteredReviews.map((review) =>
                <div key={review.id} className="w-full flex-shrink-0 px-4">
                      <div className="max-w-2xl mx-auto">
                        <ReviewCard {...review} />
                      </div>
                    </div>
                )}
                </div>
              </div>

              {/* Navigation Arrows */}
              {filteredReviews.length > 1 &&
            <>
                  <button
                onClick={prevSlide}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white hover:bg-sky-100 text-primary p-3 rounded-full shadow-lg transition-all hover:scale-110 z-10"
                aria-label="Previous review">

                    <Icon name="ChevronLeftIcon" size={24} />
                  </button>
                  <button
                onClick={nextSlide}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white hover:bg-sky-100 text-primary p-3 rounded-full shadow-lg transition-all hover:scale-110 z-10"
                aria-label="Next review">

                    <Icon name="ChevronRightIcon" size={24} />
                  </button>
                </>
            }

              {/* Dot Indicators */}
              {filteredReviews.length > 1 &&
            <div className="flex justify-center items-center space-x-2 mt-8">
                  {filteredReviews.map((_, index) =>
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`transition-all ${
                currentSlide === index ?
                'w-8 h-3 bg-primary rounded-full' : 'w-3 h-3 bg-sky-300 rounded-full hover:bg-sky-400'}`
                }
                aria-label={`Go to review ${index + 1}`} />

              )}
                </div>
            }
            </div>
          }

          {activeTab === 'cases' &&
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {caseStudies.map((study) =>
            <CaseStudyCard key={study.id} {...study} />
            )}
            </div>
          }
        </div>
      </section>

      <section className="py-16 bg-accent">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <Icon name="ChatBubbleLeftRightIcon" size={48} className="text-primary mx-auto mb-6" />
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mb-4">
              Join Our Community of Satisfied Homeowners
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Experience the Crystal Clean difference—reliable service, exceptional quality, and genuine care for your home
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="/get-quote"
                className="inline-flex items-center justify-center px-8 py-3 bg-green-600 text-white font-semibold rounded-full hover:bg-green-700 transition-smooth shadow-brand btn-bubbly">

                Get Your Free Quote
                <Icon name="ArrowRightIcon" size={20} className="ml-2" />
              </a>
              <a
                href="/services-overview"
                className="inline-flex items-center justify-center px-8 py-3 bg-white text-primary font-semibold rounded-md hover:bg-opacity-90 transition-smooth shadow-brand">

                View Our Services
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>);

}